'use client';

import { RichText } from '@/components/richText/RichText';
import { getUserReplacements } from '@/helper/replacements';
import { linkToString } from '@/helper/urls';
import { useIsMounted } from '@/hooks/useIsMounted';
import { useUserStore } from '@/providers/UserStoreProvider';
import type { PartialModalStoryblok, PartialPopupStoryblok } from '@/types/component-types-sb';
import { type ReactNode, useMemo } from 'react';

/** Shared CMS fields for global overlay content (`partial_popup` / `partial_modal`). */
export type GlobalOverlayBlok = Pick<
  PartialPopupStoryblok | PartialModalStoryblok,
  'title' | 'copy_text' | 'cta_text' | 'cta_link'
>;

export type GlobalOverlayContent = {
  title: string;
  text: ReactNode;
  ctaText?: string;
  ctaHref: string;
  ctaTarget?: string;
  hasCta: boolean;
};

/**
 * Resolves personalized overlay content from a Storyblok blok.
 * Returns `null` until the client is mounted and the user store is ready
 * (`hydrated && !loggingIn`) so placeholders are stable and CLS stays zero.
 */
export function useGlobalOverlayContent(blok: GlobalOverlayBlok): GlobalOverlayContent | null {
  const isMounted = useIsMounted();

  const { name, email, hydrated, loggingIn } = useUserStore((state) => ({
    name: state.name,
    email: state.email,
    hydrated: state.hydrated,
    loggingIn: state.loggingIn,
  }));

  const replacements = useMemo(() => getUserReplacements({ name, email }), [name, email]);

  if (!isMounted || !hydrated || loggingIn) {
    return null;
  }

  const ctaHref = linkToString(blok.cta_link);
  const ctaText = blok.cta_text;
  const hasCta = !!(ctaText && ctaHref);

  return {
    title: blok.title,
    text: <RichText text={blok.copy_text} replace={replacements} />,
    ctaText,
    ctaHref,
    ctaTarget: blok.cta_link.target,
    hasCta,
  };
}
